// DEPENDENCIES
import React, { useEffect, useRef, useState } from "react";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Tab,
	Tabs,
	TextField,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BoltIcon from "@mui/icons-material/Bolt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CallIcon from "@mui/icons-material/Call";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MailIcon from "@mui/icons-material/Mail";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";

// COMPONENTS
import MemoField from "./MemoField";
import MonthCalendar from "./MonthCalendar";
import FlagCheckboxes from "./TimeEntryFlags";

// DATA / HELPERS
import { useTimeEntryData } from "./timeEntryData";
import {
	SUGGESTION_SOURCES,
	prefillProject,
	rankProjects,
	workloadForDay,
	workloadHours,
} from "./timeEntrySuggestions";
import {
	hoursInputOk,
	MON3,
	MONTHS,
	addDays,
	fmtHours,
	fromIso,
	isoOf,
	matchesProject,
	sameDay,
	toHours,
} from "./timeEntryUtils";

// Project / activity buttons shown on a card before "+N more"
const MAX_CHIPS = 3;

// Suggestions' hours added up, to the 0.1 (email has none)
const sumHours = (list) =>
	Math.round(list.reduce((a, s) => a + toHours(s.hours), 0) * 10) / 10;

const SOURCE_ICONS = {
	email: MailIcon,
	call: CallIcon,
	calendar: EventIcon,
	workload: WorkHistoryIcon,
};

const DOW_LONG = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
	"Sunday",
];

// billable / compTime stay null until the user changes them, so they follow
// the default for the chosen project, activity and day
// Mobile pickers always open below their field (never flipping up off-screen)
const MOBILE_POPPER = {
	slotProps: {
		popper: {
			placement: "bottom-start",
			modifiers: [{ name: "flip", enabled: false }],
		},
	},
};

const EMPTY_FORM = {
	projectId: "",
	activityId: "",
	hours: "",
	memo: "",
	billable: null,
	compTime: null,
};

// FUNCTIONAL COMPONENT
export default function QuickAddDialog({
	open,
	onClose,
	week,
	quickDate,
	onPickDate,
	today,
	hoursOnDate,
	projectHoursOnDate,
	dailyTarget,
	existingCell,
	suggestions,
	onSave,
	mobile = false,
	// Project Viewer: the project can't be changed and only suggestions that
	// point to it are shown
	lockedProjectId = "",
	addLabel = "Add to Week",
	notice = null,
}) {
	// DATA
	const {
		projects,
		recentProjectIds,
		projectOf,
		activityOf,
		activitiesFor,
		activitiesState,
		ensureActivities,
		billableDefault,
	} = useTimeEntryData();

	const blankForm = { ...EMPTY_FORM, projectId: lockedProjectId };

	// STATES
	const [form, setForm] = useState(blankForm);
	// What's typed in the project box (not the chosen project's name)
	const [projectQuery, setProjectQuery] = useState("");
	// The suggestion(s) that filled the form, hidden once it's added. Several
	// when picked cards were combined into one entry.
	const [loadedSuggestions, setLoadedSuggestions] = useState([]);
	// "Your day" cards ticked to combine into one entry (ids)
	const [picked, setPicked] = useState([]);
	const [toast, setToast] = useState(null);
	const [calAnchor, setCalAnchor] = useState(null);
	// Suggestions tab (day | budget) and, on "Your day", which kind to show
	// (all | call | calendar | email). The Project Viewer opens on the budget.
	const [sugTab, setSugTab] = useState(lockedProjectId ? "budget" : "day");
	const [sugFilter, setSugFilter] = useState("all");
	// Waiting on onSave (the Project Viewer posts straight to BQE)
	const [saving, setSaving] = useState(false);
	const toastTimer = useRef(null);

	// A locked project starts in the form; a different one (another phase in
	// the Project Viewer) starts the form over
	useEffect(() => {
		if (!lockedProjectId) return;
		setLoadedSuggestions([]);
		setForm({ ...EMPTY_FORM, projectId: lockedProjectId });
		ensureActivities(lockedProjectId);
	}, [lockedProjectId, ensureActivities]);

	const dayIndex = week.findIndex((d) => sameDay(d.date, quickDate));
	const day = week[Math.max(0, dayIndex)];
	const ready =
		!saving &&
		form.projectId &&
		form.activityId &&
		toHours(form.hours) > 0 &&
		form.memo.trim();
	const existing =
		form.projectId && form.activityId
			? existingCell(form.projectId, form.activityId, quickDate)
			: null;
	const alreadyHours = existing ? toHours(existing.hours) : 0;

	// Adding to an existing entry keeps its flags; a new one starts from the
	// BQE billable default, like a new line in the grid
	const flagDefaults =
		alreadyHours > 0
			? existing
			: {
					billable:
						!!form.projectId &&
						billableDefault(form.projectId, form.activityId),
					overtime: false,
					extraTime: false,
					compTime: false,
				};
	const flagValues = {
		billable: form.billable ?? flagDefaults.billable,
		overtime: !!flagDefaults.overtime,
		extraTime: !!flagDefaults.extraTime,
		compTime: form.compTime ?? flagDefaults.compTime,
	};

	const dayIso = isoOf(quickDate);
	// Picks are for one day; another day starts over
	useEffect(() => setPicked([]), [dayIso]);
	const isLoaded = (id) => loadedSuggestions.some((s) => s.id === id);
	// With a locked project, only calls / meetings / email linked to it: a
	// HubSpot contact on its deal or its project number in the title. A project
	// name in a title, or a number last logged there, isn't a link.
	const relates = (s) =>
		!lockedProjectId ||
		(!s.weakMatch && s.projectIds.includes(lockedProjectId));
	const daySuggestions = suggestions.suggestions.filter(
		(s) => s.date === dayIso && relates(s),
	);
	const dayOutlook = suggestions.outlook.items.filter(
		(s) => s.date === dayIso && relates(s),
	);
	// Budgets with hours left that the day has no time on yet
	const dayWorkload = workloadForDay(
		suggestions.workload.budgets,
		suggestions.workload.dismissed,
		dayIso,
		(projectId) => projectHoursOnDate(projectId, quickDate),
	).filter(
		(b) =>
			projectOf(b.projectId).name !== "Unknown project" &&
			(!lockedProjectId || b.projectId === lockedProjectId),
	);
	// Calls, meetings and sent email in one list, in the order they happened
	const dayTimeline = [...daySuggestions, ...dayOutlook].sort(
		(a, b) => a.at - b.at,
	);
	const shows = (source) => sugFilter === "all" || sugFilter === source;
	const shownTimeline = dayTimeline.filter((s) => shows(s.source));
	// Picks stay across the Calls / Calendar / Email filters
	const pickedItems = dayTimeline.filter((s) => picked.includes(s.id));
	const pickedHours = sumHours(pickedItems);
	const pickedNoHours = pickedItems.filter((s) => !toHours(s.hours)).length;
	const allShownPicked =
		shownTimeline.length > 0 &&
		shownTimeline.every((s) => picked.includes(s.id));
	const sugFilters = [
		{ key: "all", label: "All", count: dayTimeline.length },
		{ key: "call", label: "Calls", count: daySuggestions.length },
		{
			key: "calendar",
			label: "Calendar",
			count: dayOutlook.filter((s) => s.source === "calendar").length,
		},
		{
			key: "email",
			label: "Email",
			count: dayOutlook.filter((s) => s.source === "email").length,
		},
	];

	const m0 = week[0].date.getMonth();
	const m6 = week[6].date.getMonth();
	const monthLabel =
		(m0 === m6 ? MONTHS[m0] : MON3[m0] + " – " + MON3[m6]) +
		" " +
		week[6].date.getFullYear();

	// INPUT HANDLERS
	const set = (patch) => setForm((f) => ({ ...f, ...patch }));

	const toggleFlag = (key) => set({ [key]: !flagValues[key] });

	// A different activity has its own billable default
	const pickActivity = (activityId) =>
		set({ activityId, billable: null, compTime: null });

	const pickProject = (projectId) => {
		// The user chooses the activity; changing project clears it
		set({ projectId, activityId: "", billable: null, compTime: null });
		setToast(null);
		ensureActivities(projectId);
	};

	const clearForm = () => {
		setLoadedSuggestions([]);
		setForm(blankForm);
		setProjectQuery("");
	};

	// onSave returns false when the entry wasn't added; it may return a
	// promise (Project Viewer) and the confirmation text to show
	const save = async () => {
		if (!ready) return false;
		setSaving(true);
		let saved;
		try {
			saved = await onSave({
				...form,
				billable: flagValues.billable,
				compTime: flagValues.compTime,
				memo: form.memo.trim(),
				date: quickDate,
			});
		} finally {
			setSaving(false);
		}
		if (saved === false) return false;
		// Hides the suggestion; for a call, also remembers the project for its
		// number. (A budget drops off the day by itself once time is logged.)
		// In a combined entry, a call's number is only remembered for the
		// project when that call already pointed to it, so one unrelated call
		// in the mix doesn't learn the wrong project
		const combined = loadedSuggestions.length > 1;
		loadedSuggestions
			.filter((s) => s.source !== "workload")
			.forEach((s) =>
				suggestions.hide(
					s,
					"used",
					!combined ||
						s.projectIds.includes(form.projectId) ||
						s.rememberedProjectId === form.projectId
						? form.projectId
						: null,
				),
			);
		clearForm();
		setToast(
			typeof saved === "string"
				? saved
				: form.hours + " hrs added to " + projectOf(form.projectId).name,
		);
		clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setToast(null), 3000);
	};

	// "Done": closes Quick Add, saving the entry first if it's ready
	const finish = async () => {
		if (ready && (await save()) === false) return;
		onClose();
	};

	// Fills the form from a suggestion: the project (and, for a budget, the
	// activity) the user clicked, else the match. Calls never fill an activity.
	const loadSuggestion = (s, projectId = "", activityId = "") => {
		setLoadedSuggestions([s]);
		setToast(null);
		setForm({
			...EMPTY_FORM,
			projectId: lockedProjectId || projectId,
			activityId,
			hours: s.hours,
			memo: s.memo,
		});
		setProjectQuery("");
		if (lockedProjectId || projectId)
			ensureActivities(lockedProjectId || projectId);
		onPickDate(fromIso(s.date));
	};

	// The project a card would fill in: the locked one, else its match
	const prefillFor = (s) =>
		lockedProjectId || prefillProject(s, rankProjects(s, projectOf));

	// Fills the form from several picked cards as one entry: their hours added
	// up and memos joined. The project (or activity) is filled in only when
	// every card has the same one; otherwise it's left for the user to pick.
	const loadPicked = () => {
		const list = pickedItems;
		if (!list.length) return;
		const same = (values) =>
			values.every((v) => v && v === values[0]) ? values[0] : "";
		const projectId = same(list.map(prefillFor));
		const activityId = projectId
			? same(list.map((s) => s.activityId || ""))
			: "";
		const hours = sumHours(list);
		setLoadedSuggestions(list);
		setPicked([]);
		setToast(null);
		setForm({
			...EMPTY_FORM,
			projectId,
			activityId,
			hours: hours ? String(hours) : "",
			memo: [
				...new Set(list.map((s) => s.memo.trim()).filter(Boolean)),
			].join("; "),
		});
		setProjectQuery("");
		if (projectId) ensureActivities(projectId);
		onPickDate(fromIso(list[0].date));
	};

	const togglePicked = (id) =>
		setPicked((ids) =>
			ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
		);

	// Ticks every card in view, or unticks them when they're all ticked
	const toggleAllShown = () => {
		const ids = shownTimeline.map((s) => s.id);
		setPicked((cur) =>
			allShownPicked
				? cur.filter((id) => !ids.includes(id))
				: [...new Set([...cur, ...ids])],
		);
	};

	// Hides this one call for good; if it's in the form, the form clears too
	const dismissSuggestion = (s) => {
		suggestions.hide(s, "dismissed");
		setPicked((ids) => ids.filter((id) => id !== s.id));
		if (isLoaded(s.id)) clearForm();
	};

	// Hides a budget for this day only
	const dismissWorkload = (projectId) => {
		suggestions.dismissWorkload(projectId, dayIso);
		if (isLoaded("workload:" + projectId)) clearForm();
	};

	// One call / calendar / email card
	const renderCard = (s) => {
		const ranked = lockedProjectId
			? [lockedProjectId]
			: rankProjects(s, projectOf);
		return (
			<SuggestionCard
				key={s.id}
				suggestion={s}
				projects={ranked}
				prefillId={lockedProjectId || prefillProject(s, ranked)}
				projectOf={projectOf}
				loaded={isLoaded(s.id)}
				picked={picked.includes(s.id)}
				onPick={() => togglePicked(s.id)}
				onReview={(projectId) => loadSuggestion(s, projectId)}
				onDismiss={() => dismissSuggestion(s)}
				onRemoveFromForm={clearForm}
			/>
		);
	};

	// Lines under the day's timeline for a source that's still loading, needs a
	// Microsoft sign-in or failed; or "nothing on this day" when all is loaded
	const timelineNotes = () => {
		const note = (key, text, action) => (
			<div key={key} className="teSug-none">
				{text}
				{action}
			</div>
		);
		const retry = (onClick, label = "Try again") => (
			<Button size="small" onClick={onClick}>
				{label}
			</Button>
		);
		const notes = [];
		const showCalls = shows("call");
		const showOutlook = shows("calendar") || shows("email");
		if (!showCalls) {
			// (calls hidden by the filter)
		} else if (suggestions.status === "loading") {
			notes.push(note("calls", "Loading your calls…"));
		} else if (suggestions.status === "no-user") {
			notes.push(note("calls", "No Dialpad account found for your email"));
		} else if (suggestions.status === "error") {
			notes.push(note("calls", suggestions.error, retry(suggestions.reload)));
		}
		const outlook = suggestions.outlook;
		if (!showOutlook) {
			// (calendar and email hidden by the filter)
		} else if (outlook.status === "loading") {
			notes.push(note("outlook", "Loading your calendar and email…"));
		} else if (outlook.status === "signin") {
			notes.push(
				note(
					"outlook",
					"Sign in to Microsoft again to see calendar and email suggestions",
					retry(suggestions.connectOutlook, "Sign in"),
				),
			);
		} else if (outlook.status === "error") {
			notes.push(note("outlook", outlook.error, retry(suggestions.reloadOutlook)));
		}
		if (!notes.length && !shownTimeline.length) {
			const what = {
				all: "calls, meetings or sent email",
				call: "calls",
				calendar: "meetings",
				email: "sent email",
			}[sugFilter];
			notes.push(
				note(
					"none",
					lockedProjectId
						? `No ${what} matched to this project on this day`
						: `No ${what} on this day`,
				),
			);
		}
		return notes;
	};

	// Project picker, same as "Add a project": your recent projects until you
	// type, then matches from every project
	const recentProjects = recentProjectIds.map(projectOf);
	const projectPickerProps = {
		disabled: !!lockedProjectId || saving,
		options: projects,
		value: form.projectId ? projectOf(form.projectId) : null,
		onChange: (_, p) => pickProject(p ? p.id : ""),
		onInputChange: (_, text, reason) =>
			setProjectQuery(reason === "input" ? text : ""),
		getOptionLabel: (p) => p.name,
		isOptionEqualToValue: (a, b) => a.id === b.id,
		filterOptions: (opts) => {
			const q = projectQuery.trim().toLowerCase();
			if (q) return opts.filter((p) => matchesProject(p, q));
			return recentProjects.length ? recentProjects : opts;
		},
		groupBy: () =>
			projectQuery.trim() || !recentProjects.length
				? "Matches"
				: "Recent for you",
		renderGroup: (params) => (
			<li key={params.key}>
				<div className="teAddStrip-listHead">{params.group}</div>
				<ul className="teProjectPicker-group">{params.children}</ul>
			</li>
		),
		renderOption: (props, p) => (
			<li {...props} key={p.id}>
				<span className="teOption">
					<span className="teOption-name">{p.name}</span>
				</span>
			</li>
		),
	};

	// Activity picker: search by name or description, same box on web and mobile
	const activityPickerProps = {
		options: activitiesFor(form.projectId),
		value: form.activityId ? activityOf(form.activityId) : null,
		onChange: (_, a) => pickActivity(a ? a.id : ""),
		getOptionLabel: (a) => a.name,
		isOptionEqualToValue: (a, b) => a.id === b.id,
		filterOptions: (opts, { inputValue }) => {
			const q = inputValue.trim().toLowerCase();
			return q
				? opts.filter((a) =>
						(a.name + " " + a.description).toLowerCase().includes(q),
					)
				: opts;
		},
		disabled: !form.projectId || saving,
		renderOption: (props, a) => (
			<li {...props} key={a.id}>
				<span className="teOption">
					<span className="teOption-name">{a.name}</span>
					{a.description !== a.name && (
						<span className="teOption-sub">{a.description}</span>
					)}
				</span>
			</li>
		),
	};

	const activityPlaceholder = !form.projectId
		? "Select a project first"
		: activitiesState(form.projectId) === "error"
			? "Couldn't load activities. Pick the project again."
			: activitiesState(form.projectId) === "ready"
				? "Select an activity"
				: "Loading activities…";

	// Warns (top of the form) that the hours will merge into an existing entry
	const alreadyAlert = alreadyHours > 0 && (
		<Alert severity="warning" className="teQuick-alert">
			<strong>{fmtHours(alreadyHours)} hrs already logged</strong> on{" "}
			{activityOf(form.activityId).name} for{" "}
			{day.isToday ? "today" : day.long}. These hours and memo will be
			added to it.
		</Alert>
	);

	// RENDER — mobile: full-screen form laid out like the mobile mockup
	// (labels above fields, pinned footer). Logs to the day picked on the page.
	if (mobile) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				keepMounted
				fullScreen
				PaperProps={{ className: "teQuick teQM" }}
			>
				<div className="teQM-head">
					<IconButton aria-label="Close" onClick={onClose}>
						<CloseIcon />
					</IconButton>
					<BoltIcon className="teQM-icon" color="primary" />
					<span className="teQM-titles">
						<span className="teQM-title">Quick Add</span>
						<span className="teQM-sub">
							{(day.isToday ? "Today · " : "") +
								DOW_LONG[Math.max(0, dayIndex)] +
								", " +
								MON3[day.date.getMonth()] +
								" " +
								day.date.getDate()}
						</span>
					</span>
				</div>

				<div className="teQM-body">
					{notice}
					{alreadyAlert}
					<div className="teQM-field">
						<span className="teQM-label">Project</span>
						<Autocomplete
							{...projectPickerProps}
							{...MOBILE_POPPER}
							renderInput={(params) => (
								<TextField
									{...params}
									className="teQM-input"
									placeholder="Select a project"
								/>
							)}
						/>
					</div>

					<div className="teQM-field">
						<span className="teQM-label">Activity</span>
						{/* Same search-and-pick box as Project */}
						<Autocomplete
							{...activityPickerProps}
							{...MOBILE_POPPER}
							noOptionsText={activityPlaceholder}
							renderInput={(params) => (
								<TextField
									{...params}
									className="teQM-input"
									placeholder={activityPlaceholder}
								/>
							)}
						/>
						<span className="teQM-desc">
							{form.activityId
								? activityOf(form.activityId).description
								: ""}
						</span>
					</div>

					<div className="teQM-field">
						<span className="teQM-label">Hours</span>
						<TextField
							className="teQM-input teQM-hours"
							value={form.hours}
							placeholder="0.0"
							inputProps={{ inputMode: "decimal" }}
							onChange={(e) =>
								hoursInputOk(e.target.value) &&
								set({ hours: e.target.value })
							}
						/>
					</div>

					<FlagCheckboxes
						className="teM-flags"
						itemClassName="teM-flag"
						values={flagValues}
						onToggle={toggleFlag}
						disabled={!form.projectId}
					/>

					{/* Memo fills the rest of the screen */}
					<div className="teQM-field teQM-field--grow">
						<span className="teQM-label">Memo</span>
						<TextField
							className="teQM-input teQM-memo"
							multiline
							rows={4}
							value={form.memo}
							placeholder="What was done, in your own words…"
							onChange={(e) => set({ memo: e.target.value })}
						/>
					</div>

					{toast && (
						<span className="teQuick-toast">
							<CheckCircleIcon fontSize="small" />
							{toast}
						</span>
					)}
				</div>

				<div className="teQM-foot">
					<Button variant="outlined" disabled={saving} onClick={finish}>
						{ready ? "Add & Done" : "Done"}
					</Button>
					<Button
						variant="contained"
						disableElevation
						startIcon={<CheckIcon />}
						disabled={!ready}
						onClick={save}
					>
						{saving ? "Sending…" : addLabel}
					</Button>
				</div>
			</Dialog>
		);
	}

	// RENDER — web
	return (
		<Dialog
			open={open}
			onClose={onClose}
			keepMounted
			maxWidth={false}
			PaperProps={{ className: "teQuick" }}
		>
			<DialogTitle className="teDialogTitle teQuick-title">
				<BoltIcon />
				<span className="teQuick-titleText">Quick Add</span>
				<Tooltip title="Close" arrow placement="top">
					<IconButton size="small" color="inherit" onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Tooltip>
			</DialogTitle>

			<DialogContent className="teQuick-body">
				{/* Day strip */}
				<div className="teQuick-days">
					<Tooltip title="Previous week" arrow placement="top">
						<IconButton
							onClick={() => onPickDate(addDays(quickDate, -7))}
						>
							<ChevronLeftIcon />
						</IconButton>
					</Tooltip>
					<div className="teQuick-dayGrid">
						{week.map((d, i) => {
							const hrs = hoursOnDate(d.date);
							const on = i === dayIndex;
							const cls = [
								"teQuick-day",
								on && "teQuick-day--on",
								d.isToday && "teQuick-day--today",
								d.isWeekend && "teQuick-day--weekend",
							]
								.filter(Boolean)
								.join(" ");
							const hrsCls = [
								"teQuick-dayHrs",
								!hrs && "teQuick-dayHrs--none",
							]
								.filter(Boolean)
								.join(" ");
							return (
								<button
									type="button"
									key={d.iso}
									className={cls}
									onClick={() => onPickDate(d.date)}
								>
									<span className="teQuick-dayName">
										{d.name} {d.date.getDate()}
									</span>
									<span className={hrsCls}>
										{hrs ? fmtHours(hrs) + " hrs" : "—"}
									</span>
								</button>
							);
						})}
					</div>
					<Tooltip title="Next week" arrow placement="top">
						<IconButton
							onClick={() => onPickDate(addDays(quickDate, 7))}
						>
							<ChevronRightIcon />
						</IconButton>
					</Tooltip>
					<span className="teQuick-sep" />
					<Button
						className="teDateBtn teDateBtn--sm"
						color="inherit"
						startIcon={<CalendarMonthIcon color="primary" />}
						endIcon={<ExpandMoreIcon className="teMutedIcon" />}
						onClick={(e) => setCalAnchor(e.currentTarget)}
					>
						{monthLabel}
					</Button>
					<MonthCalendar
						anchorEl={calAnchor}
						onClose={() => setCalAnchor(null)}
						mode="day"
						selectedDate={quickDate}
						today={today}
						hasHours={(d) => hoursOnDate(d) > 0}
						onPick={(d) => {
							onPickDate(d);
							setCalAnchor(null);
						}}
						footerLabel="Today"
						onFooter={() => {
							onPickDate(today);
							setCalAnchor(null);
						}}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "right",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "right",
						}}
					/>
				</div>

				<div className="teQuick-columns">
					{/* Form */}
					<div
						className={
							"teQuick-form" +
							(loadedSuggestions.length ? " teQuick-form--loaded" : "")
						}
					>
						<div className="teQuick-formHead">
							<span className="teQuick-formTitle">
								Log an entry
							</span>
							<span className="teQuick-formDay">
								{(day.isToday ? "Today · " : "") + day.long}
							</span>
							{loadedSuggestions.length > 0 && (
								<span className="teChip teChip--blue">
									{loadedSuggestions.length > 1
										? `From ${loadedSuggestions.length} suggestions`
										: "From suggestion"}
									<Tooltip
										title="Clear form"
										arrow
										placement="top"
									>
										<IconButton
											size="small"
											color="inherit"
											sx={{ p: 0.25 }}
											onClick={clearForm}
										>
											<CloseIcon sx={{ fontSize: 15 }} />
										</IconButton>
									</Tooltip>
								</span>
							)}
						</div>

						{notice}
						{alreadyAlert}
						<div className="teQuick-fields">
							<Autocomplete
								size="small"
								{...projectPickerProps}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Project"
										placeholder="Search projects…"
									/>
								)}
							/>
							<Autocomplete
								size="small"
								{...activityPickerProps}
								noOptionsText={activityPlaceholder}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Activity"
										placeholder="Search activities…"
										helperText={
											form.activityId
												? activityOf(form.activityId)
														.description
												: activityPlaceholder
										}
									/>
								)}
							/>
							<TextField
								size="small"
								className="teQuick-hours"
								label="Hours"
								value={form.hours}
								placeholder="0"
								inputProps={{
									inputMode: "decimal",
									className: "teHoursInput",
								}}
								onChange={(e) =>
									hoursInputOk(e.target.value) &&
									set({ hours: e.target.value })
								}
							/>
							<FlagCheckboxes
								small
								className="teQuick-flags"
								values={flagValues}
								onToggle={toggleFlag}
								disabled={!form.projectId}
							/>
						</div>

						<MemoField
							className="teQuick-memo"
							multiline
							minRows={2}
							size="small"
							label="Memo"
							value={form.memo}
							placeholder="What was done, in your own words…"
							onChange={(e) => set({ memo: e.target.value })}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									save();
								}
							}}
						/>

						<div className="teQuick-actions">
							<Button
								variant="outlined"
								disabled={saving}
								onClick={finish}
							>
								{ready ? "Add & Done" : "Done"}
							</Button>
							<Button
								variant="contained"
								disableElevation
								startIcon={<CheckIcon />}
								disabled={!ready}
								onClick={save}
							>
								{saving ? "Sending…" : addLabel}
							</Button>
							{toast && (
								<span className="teQuick-toast">
									<CheckCircleIcon fontSize="small" />
									{toast}
								</span>
							)}
						</div>
					</div>

					{/* Suggestions */}
					<div className="teSug">
						<div className="teSug-head">
							<div className="teSug-headText">
								<span className="teSug-title">
									<AutoAwesomeIcon
										color="primary"
										fontSize="small"
									/>
									Suggested entries
									<span className="teSug-date">{day.long}</span>
								</span>
							</div>
						</div>
						<Tabs
							className="teSug-tabs"
							value={sugTab}
							onChange={(_, v) => setSugTab(v)}
							variant="fullWidth"
						>
							<Tab
								value="day"
								icon={<ScheduleIcon sx={{ fontSize: 17 }} />}
								iconPosition="start"
								label={`Your day (${dayTimeline.length})`}
							/>
							<Tab
								value="budget"
								icon={<WorkHistoryIcon sx={{ fontSize: 17 }} />}
								iconPosition="start"
								label={`Budget hours left (${dayWorkload.length})`}
							/>
						</Tabs>
						{sugTab === "day" && (
							<div className="teSug-filters">
								{sugFilters.map(({ key, label, count }) => (
									<Chip
										key={key}
										className="teSug-filter"
										label={
											<>
												{label}
												<span className="teSug-filterCount">
													{count}
												</span>
											</>
										}
										color={sugFilter === key ? "primary" : "default"}
										variant={sugFilter === key ? "filled" : "outlined"}
										onClick={() => setSugFilter(key)}
									/>
								))}
							</div>
						)}
						{suggestions.matchError && (
							<Alert severity="warning" className="teSug-alert">
								Couldn't check HubSpot for matching projects, so
								some suggestions are shown without them.
							</Alert>
						)}
						{/* Tick cards to combine them into one entry */}
						{sugTab === "day" && shownTimeline.length > 0 && (
							<div
								className={
									"teSug-pickBar" +
									(pickedItems.length ? " teSug-pickBar--on" : "")
								}
							>
								<Checkbox
									size="small"
									className="teSug-pick"
									checked={allShownPicked}
									indeterminate={
										!allShownPicked &&
										shownTimeline.some((s) => picked.includes(s.id))
									}
									onChange={toggleAllShown}
									inputProps={{ "aria-label": "Select all shown" }}
								/>
								<span className="teSug-pickText">
									{pickedItems.length
										? `${pickedItems.length} selected · ${fmtHours(pickedHours)} hrs` +
											(pickedNoHours
												? ` (${pickedNoHours} without hours)`
												: "")
										: "Select several to combine into one entry"}
								</span>
								{pickedItems.length > 0 && (
									<>
										<Button
											size="small"
											color="inherit"
											className="teTextBtn"
											onClick={() => setPicked([])}
										>
											Clear
										</Button>
										<Button
											size="small"
											variant="contained"
											disableElevation
											startIcon={<ChevronLeftIcon />}
											onClick={loadPicked}
										>
											Add to form
										</Button>
									</>
								)}
							</div>
						)}
						<div className="teSug-list">
							{sugTab === "day" && (
								<>
									{shownTimeline.map(renderCard)}
									{timelineNotes()}
								</>
							)}

							{sugTab === "budget" && (
								<>
									{dayWorkload.map((b) => {
										const id = "workload:" + b.projectId;
										return (
											<WorkloadCard
												key={b.projectId}
												budget={b}
												projectName={projectOf(b.projectId).name}
												hours={workloadHours(
													b,
													day.isWeekend ? 0 : dailyTarget,
													hoursOnDate(quickDate),
												)}
												loaded={isLoaded(id)}
												onAdd={(hours, activityId) =>
													loadSuggestion(
														{
															id,
															source: "workload",
															hours,
															memo: "",
															date: dayIso,
														},
														b.projectId,
														activityId,
													)
												}
												onDismiss={() => dismissWorkload(b.projectId)}
												onRemoveFromForm={clearForm}
											/>
										);
									})}
									{dayWorkload.length === 0 && (
										<div className="teSug-none">
											{suggestions.workload.status === "loading"
												? "Loading your budgets…"
												: suggestions.workload.status === "error"
													? "Couldn't load your budgeted hours."
													: lockedProjectId
													? "No budgeted hours left for you on this project"
													: "No budgeted hours left to log on this day"}
											{suggestions.workload.status === "error" && (
												<Button
													size="small"
													onClick={suggestions.reloadWorkload}
												>
													Try again
												</Button>
											)}
										</div>
									)}
								</>
							)}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

// One suggested entry: the call, its possible projects and dismiss options
function SuggestionCard({
	suggestion: s,
	projects,
	prefillId,
	projectOf,
	loaded,
	picked,
	onPick,
	onReview,
	onDismiss,
	onRemoveFromForm,
}) {
	// "+N more" opens the full list of possible projects on the card
	const [showAllProjects, setShowAllProjects] = useState(false);
	const [confirming, setConfirming] = useState(false);

	const SourceIcon = SOURCE_ICONS[s.source];
	const possible = prefillId ? [] : projects;
	const chips = showAllProjects
		? possible
		: possible.slice(0, MAX_CHIPS);
	const more = possible.length - MAX_CHIPS;
	const remembered = prefillId && prefillId === s.rememberedProjectId;

	return (
		<div
			className={
				"teSug-card" +
				(loaded ? " teSug-card--loaded" : "") +
				(picked ? " teSug-card--picked" : "")
			}
		>
			<div className="teSug-top">
				<Checkbox
					size="small"
					className="teSug-pick"
					checked={picked}
					onChange={onPick}
					inputProps={{ "aria-label": "Select to combine" }}
				/>
				<span className="teSug-icons">
					<SourceIcon
						titleAccess={SUGGESTION_SOURCES[s.source].label}
						sx={{ fontSize: 16 }}
					/>
				</span>
				<span className="teSug-summary">{s.summary}</span>
				<span className="teSug-hours">
					{s.hours || "—"}
					<span> hrs</span>
				</span>
			</div>
			<div className="teSug-what">
				<span className="teSug-project">{s.title}</span>
				{s.detail && <span className="teSug-activity">{s.detail}</span>}
			</div>
			{prefillId && (
				<div className="teSug-match">
					<span className="teSug-matchName">
						{projectOf(prefillId).name}
					</span>
					<span className="teSug-matchWhy">
						{remembered
							? "You logged this number here last time"
							: s.matchLabel}
					</span>
				</div>
			)}
			{chips.length > 0 && (
				<div className="teSug-match">
					<span className="teSug-matchWhy">
						Possible projects · {s.matchLabel}
					</span>
					<div className="teSug-chips">
						{chips.map((id) => (
							<Tooltip
								key={id}
								title={projectOf(id).name}
								arrow
								placement="top"
							>
								<Button
									size="small"
									variant="outlined"
									className="teSug-chip"
									onClick={() => onReview(id)}
								>
									{projectOf(id).name}
								</Button>
							</Tooltip>
						))}
						{more > 0 && (
							<Button
								size="small"
								color="inherit"
								className="teTextBtn"
								onClick={() => setShowAllProjects((v) => !v)}
							>
								{showAllProjects ? "Show fewer" : `+${more} more`}
							</Button>
						)}
					</div>
				</div>
			)}
			<div className="teSug-actions">
				<Button
					size="small"
					variant={loaded ? "text" : "outlined"}
					startIcon={loaded ? <CheckIcon /> : <ChevronLeftIcon />}
					disabled={loaded}
					onClick={() => onReview(prefillId)}
				>
					{loaded ? "In form" : "Add to form"}
				</Button>
				<span className="teSug-dismiss">
					<Tooltip title="Dismiss suggestion" arrow placement="top">
						<IconButton
							size="small"
							onClick={() => setConfirming((v) => !v)}
						>
							<DeleteOutlineIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					{loaded && (
						<Tooltip title="Remove from form" arrow placement="top">
							<IconButton size="small" onClick={onRemoveFromForm}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</span>
			</div>
			{confirming && (
				<DismissConfirm
					text="Dismiss this suggestion? It won't come back."
					onConfirm={onDismiss}
					onCancel={() => setConfirming(false)}
				/>
			)}
		</div>
	);
}

// One budget with hours left: fills the project and the rest of the day's
// target. A single budgeted activity is filled in too; with several, each is
// a button that fills the form with it.
function WorkloadCard({
	budget: b,
	projectName,
	hours,
	loaded,
	onAdd,
	onDismiss,
	onRemoveFromForm,
}) {
	// "+N more" opens the full list of budgeted activities on the card
	const [showAllActivities, setShowAllActivities] = useState(false);
	const [confirming, setConfirming] = useState(false);

	const only = b.activities.length === 1 ? b.activities[0] : null;
	const choices = only ? [] : b.activities;
	const chips = showAllActivities
		? choices
		: choices.slice(0, MAX_CHIPS);
	const more = choices.length - MAX_CHIPS;

	return (
		<div className={"teSug-card" + (loaded ? " teSug-card--loaded" : "")}>
			<div className="teSug-top">
				<span className="teSug-icons">
					<WorkHistoryIcon
						titleAccess={SUGGESTION_SOURCES.workload.label}
						sx={{ fontSize: 16 }}
					/>
				</span>
				<span className="teSug-summary">
					{fmtHours(b.remainingHours)} of {fmtHours(b.budgetedHours)}{" "}
					budgeted hrs left
				</span>
			</div>
			<div className="teSug-what">
				<span className="teSug-project">{projectName}</span>
				{only && (
					<span className="teSug-activity">Budgeted as {only.name}</span>
				)}
			</div>
			{chips.length > 0 && (
				<div className="teSug-match">
					<span className="teSug-matchWhy">Budgeted activities</span>
					<div className="teSug-chips">
						{chips.map((a) => (
							<Tooltip
								key={a.id}
								title={a.description}
								arrow
								placement="top"
							>
								<Button
									size="small"
									variant="outlined"
									className="teSug-chip"
									onClick={() => onAdd(hours, a.id)}
								>
									{a.name}
								</Button>
							</Tooltip>
						))}
						{more > 0 && (
							<Button
								size="small"
								color="inherit"
								className="teTextBtn"
								onClick={() => setShowAllActivities((v) => !v)}
							>
								{showAllActivities ? "Show fewer" : `+${more} more`}
							</Button>
						)}
					</div>
				</div>
			)}
			<div className="teSug-actions">
				<Button
					size="small"
					variant={loaded ? "text" : "outlined"}
					startIcon={loaded ? <CheckIcon /> : <ChevronLeftIcon />}
					disabled={loaded}
					onClick={() => onAdd(hours, only ? only.id : "")}
				>
					{loaded ? "In form" : "Add to form"}
				</Button>
				<span className="teSug-dismiss">
					<Tooltip title="Dismiss for this day" arrow placement="top">
						<IconButton
							size="small"
							onClick={() => setConfirming((v) => !v)}
						>
							<DeleteOutlineIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					{loaded && (
						<Tooltip title="Remove from form" arrow placement="top">
							<IconButton size="small" onClick={onRemoveFromForm}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</span>
			</div>
			{confirming && (
				<DismissConfirm
					text="Dismiss this budget for this day?"
					onConfirm={onDismiss}
					onCancel={() => setConfirming(false)}
				/>
			)}
		</div>
	);
}

// "Are you sure" row inside a suggestion card
function DismissConfirm({ text, onConfirm, onCancel }) {
	return (
		<div className="teSug-ask">
			<span className="teSug-askText">{text}</span>
			<Button size="small" variant="outlined" color="error" onClick={onCancel}>
				Cancel
			</Button>
			<Button
				size="small"
				variant="contained"
				color="error"
				disableElevation
				onClick={onConfirm}
			>
				Dismiss
			</Button>
		</div>
	);
}
